import React, { useRef, useState } from 'react';
import ReCAPTCHA from 'react-google-recaptcha';
import { Grid, Stack, Typography } from '@mui/material';
import { TextField } from '@/components/forms/inputs/TextField/TextField';
import { registerFormModel } from '@/components/forms/forms/registration/registerFormModel';
import { CheckboxField } from '@/components/forms/inputs/CheckBoxField/CheckboxField';
import { ErrorAlert } from '@/components/forms/alerts/ErrorAlert/ErrorAlert';
import { CONTACT_DETAILS_PHRASES } from '@/consts/phrases/registrationPhrases';
import { usePhrase } from '@/hooks';
import { useForm } from 'react-hook-form';
import { RegistrationStep } from '@/types/enums/forms/registration/registrationWizard';
import { getRegDataOrNonMemberPersonalData } from '@/components/forms/forms/registration/registerUtils';
import { PreviousNextButtons } from '@/components/forms/other/PreviousNextButtons/PreviousNextButtons';
import { useRegistrationStore } from '@/store/pages/registrationStore/registrationStore';
import { sendMobileCode, submitContactDetailsData } from './contactDetailsFormUtils';
import { IAllRegistrationData, IWalletPersonalData } from '@/types/types';
import { WithSendCode } from '@/components/forms/wrappers/WithSendCode/WithSendCode';
import { useServerErrors } from '@/hooks/forms';
import { useConfigStore } from '@/store';
import { FormGridErrors } from '@/components/forms/alerts/FormGridErrors/FormGridErrors';
import { InfoTooltip } from '@/components/general/InfoTooltip/InfoTooltip';
import { GENERAL_PHRASES } from '@/consts/phrases/generalPhrases';

type ContactDetailsFormModel = (typeof registerFormModel)[RegistrationStep.ContactDetails];
type ContactDetailsFormFields = keyof ContactDetailsFormModel;
export type ContactDetailsFormValues = Record<ContactDetailsFormFields, string | boolean>;

interface ContactDetailsFormProps {
    registrationData?: IAllRegistrationData;
    nonMemberPersonalData?: Partial<IWalletPersonalData>;
    revalidateRegistrationData: () => Promise<void>;
}

export const ContactDetailsForm = ({
    registrationData,
    nonMemberPersonalData,
    revalidateRegistrationData,
}: ContactDetailsFormProps) => {
    const { t } = usePhrase();

    const [phoneVerificationErrorMessage, setPhoneVerificationErrorMessage] = useState<string>();
    const [recaptchaError, setRecaptchaError] = useState<number>();
    const contactDetailsModel = registerFormModel[RegistrationStep.ContactDetails];
    const { mobilePhone, mobilePhoneCode, alternativePhone, SMSOptIn } = contactDetailsModel;

    const reCaptchaRef = useRef<ReCAPTCHA>(null);
    const recaptchaKey = useConfigStore((state) => state.website_google_recaptcha_v2_site_key);

    const { goToPrevStep, goToNextStep, registrationId, resetRegistrationStore } =
        useRegistrationStore((state) => ({
            goToPrevStep: state.goToPrevStep,
            goToNextStep: state.goToNextStep,
            resetRegistrationStore: state.reset,
            registrationId: state.registrationId,
        }));

    const getRegOrNonMemberData = getRegDataOrNonMemberPersonalData(
        registrationData,
        nonMemberPersonalData,
    );

    const defaultValues = {
        mobilePhone: getRegOrNonMemberData('mobilePhone'),
        mobilePhoneCode: '',
        alternativePhone: getRegOrNonMemberData('alternativePhone', 'phone'),
        SMSOptIn: registrationData?.SMSOptIn ?? true,
    };

    const { control, handleSubmit, getValues, formState, watch, setError, clearErrors } = useForm<
        Partial<ContactDetailsFormValues>
    >({
        defaultValues,
    });
    const { errors, isSubmitting } = formState;

    const { handleServerErrors, serverErrors, clearAllErrors } = useServerErrors({
        setError,
        clearErrors,
        getValues,
    });

    const mobilePhoneFormVal = watch('mobilePhone') as string;

    const handleSendMobileCode = async () => {
        const hasSentMobileCode = await sendMobileCode(
            registrationId,
            mobilePhoneFormVal,
            reCaptchaRef?.current?.getValue(),
            setPhoneVerificationErrorMessage,
            registrationData?.countryCode || nonMemberPersonalData?.countryCode,
            handleServerErrors,
            clearAllErrors,
            t,
        );
        return hasSentMobileCode;
    };

    const handleSubmitData = async (vals: Partial<ContactDetailsFormValues>) =>
        await submitContactDetailsData(
            registrationId,
            reCaptchaRef?.current?.getValue(),
            setRecaptchaError,
            vals,
            goToNextStep,
            resetRegistrationStore,
            handleServerErrors,
            revalidateRegistrationData,
        );

    return (
        <form onSubmit={handleSubmit(handleSubmitData)} noValidate>
            <Grid container spacing={2}>
                <FormGridErrors
                    errors={serverErrors}
                    gridSx={{ mb: 2 }}
                    generalErrorId="message_box"
                />
                <Grid size={12}>
                    <Stack direction="row" spacing={1}>
                        <Typography variant="subtitle2">
                            {t(CONTACT_DETAILS_PHRASES.PHONE_ADDITION_TEXT)}
                        </Typography>
                    </Stack>
                </Grid>
                <Grid size={12}>
                    <ReCAPTCHA
                        ref={reCaptchaRef}
                        sitekey={recaptchaKey}
                        data-testid="recaptcha"
                        className="google-recaptcha"
                    />
                    {recaptchaError && (
                        <ErrorAlert
                            id="recaptcha-error"
                            errorMessage={t(recaptchaError, { asString: true })}
                        />
                    )}
                </Grid>
                <Grid size={12}>
                    <WithSendCode
                        hasSendCode={true}
                        onSendCode={handleSendMobileCode}
                        codeType="phone"
                        errorMessage={
                            phoneVerificationErrorMessage
                                ? phoneVerificationErrorMessage
                                : undefined
                        }
                        codeFormInput={
                            <TextField
                                name={mobilePhoneCode.name}
                                label={mobilePhoneCode.label}
                                control={control}
                                error={errors.mobilePhoneCode}
                                required
                                autoFocus={false}
                                fullWidth
                            />
                        }
                    >
                        <TextField
                            fullWidth
                            type="phone"
                            name={mobilePhone.name}
                            label={mobilePhone.label}
                            control={control}
                            error={errors.mobilePhone}
                            required
                            slotProps={{
                                input: {
                                    endAdornment: (
                                        <InfoTooltip title={GENERAL_PHRASES.PHONE_INFO_TOOLTIP} />
                                    ),
                                },
                            }}
                        />
                    </WithSendCode>
                </Grid>
                <Grid size={12}>
                    <TextField
                        control={control}
                        name={alternativePhone.name}
                        label={alternativePhone.label}
                        type="text"
                        fullWidth
                        error={errors.alternativePhone}
                        data-testid="alternativephone-input"
                    />
                </Grid>
                <Grid size={12}>
                    <CheckboxField
                        control={control}
                        name={SMSOptIn.name}
                        label={SMSOptIn.label}
                        error={errors.SMSOptIn}
                    />
                </Grid>
            </Grid>
            <Grid
                container
                sx={{
                    justifyContent: 'flex-end',
                    marginTop: 2,
                }}
            >
                <PreviousNextButtons
                    onPreviousClicked={goToPrevStep}
                    nextDisabled={isSubmitting}
                    previousDisabled={isSubmitting}
                />
            </Grid>
        </form>
    );
};
